// Compact inner-page hero: a soft skyline-tinted band with a bilingual eyebrow,
// large heading and a lead line. Used by Services / Network / Partners (the About
// page has its own taller hero). Page-only component owned by Agent D.
//
// zh-first: in Chinese mode the heading leads and the Latin caption follows below;
// in en mode the caption sits above (classic eyebrow).
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { fadeUp, stagger } from "../motion/variants";

interface PageHeroProps {
  eyebrowEn: string;
  title: string;
  subtitle?: string;
  /** Faint skyline image behind the band. */
  withSkyline?: boolean;
}

export default function PageHero({
  eyebrowEn,
  title,
  subtitle,
  withSkyline = true,
}: PageHeroProps) {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh") ?? true;

  const cap = (
    <motion.p
      variants={fadeUp}
      className={`font-sans text-[var(--text-sm)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[var(--brand-azure-text)]${
        isZh ? " mt-3" : ""
      }`}
    >
      {eyebrowEn}
    </motion.p>
  );
  const head = (
    <motion.h1
      variants={fadeUp}
      className={`font-zh text-[var(--text-4xl)] font-bold leading-[var(--leading-heading)] text-[var(--ink)]${
        isZh ? "" : " mt-4"
      }`}
    >
      {title}
    </motion.h1>
  );

  return (
    <section className="relative isolate overflow-hidden bg-[var(--brand-tint-3)]">
      {withSkyline && (
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/skyline-wide.jpg"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-bottom opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 to-[var(--brand-tint-3)]" />
        </div>
      )}
      <div className="mx-auto w-full max-w-6xl px-6 py-20">
        <motion.div variants={stagger} initial="hidden" animate="visible" className="max-w-3xl">
          {isZh ? (
            <>
              {head}
              {cap}
            </>
          ) : (
            <>
              {cap}
              {head}
            </>
          )}
          {subtitle && (
            <motion.p
              variants={fadeUp}
              className="mt-5 text-[var(--text-lg)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]"
            >
              {subtitle}
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
